import React from "react";
import { BrPageContext } from "@bloomreach/react-sdk";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useMediaQuery } from "react-responsive";
import { getPageTitle } from "../../../../helpers/GlobalHelpers";
import { Desktop, Mobile } from "../../../../media-queries/MediaQueries";
import { GetGeneralLinkUrl } from "../../../../helpers/LinkHelper";
import LinkWrapper from "../../../Global/LinkWrapper";
import Description from "../../../Global/Description";
import "./BasicContentTile.scss";
import Image from "../../../Global/Image/Image";
import Title from "../../../Global/Title/Title";
import { HEADING_LEVELS } from "../../../Global/GlobalConstants";

const CondensedDesktop = ({ children }) => {
  const isDesktop = useMediaQuery({ minWidth: 577 });
  return isDesktop ? children : null;
};

const CondensedMobile = ({ children }) => {
  const isMobile = useMediaQuery({ maxWidth: 576 });
  return isMobile ? children : null;
};

function BasicContentTile({ tile, componentStyle, basicHeight, basicWidth, isCondensed, condensedStyle, tileWidthOverride, tileStyleOverrides, tileMobileButtonText, tileMobileButtonTextDefault }) {
  const sendDataToGTM = useGTMDispatch();
  const page = React.useContext(BrPageContext);
  if (!tile) return null;

  const NO_IMAGE_CONDENSED = "textOnly";
  const FEATURE_CONDENSED = "feature";
  const resolvedCondensedStyle = isCondensed ? (condensedStyle || "").trim() : "";
  const noImage = resolvedCondensedStyle.toLowerCase() === NO_IMAGE_CONDENSED.toLowerCase();

  const tileWidthOverrideF = parseFloat(tileWidthOverride);
  const clampedCondensedWidth = (() => {
    if (!Number.isFinite(tileWidthOverrideF) || tileWidthOverrideF === 0) {
      return 100;
    }

    if (tileWidthOverrideF < 0) {
      return 100;
    }

    return tileWidthOverrideF;
  })();
  // Keep condensed images constrained to the image slot while still allowing authored width tuning.
  const condensedImageOverride = (() => {
    if (!isCondensed) {
      return null;
    }

    if (resolvedCondensedStyle.toLowerCase() === FEATURE_CONDENSED) {
      return {
        width: `${clampedCondensedWidth}%`,
        height: `${clampedCondensedWidth}%`,
        left: 0,
        top: "50%",
        transform: "translateY(-50%)",
        maxWidth: "none",
        maxHeight: "none",
      };
    }

    return {
      width: `${clampedCondensedWidth}%`,
      right: `${(100 - clampedCondensedWidth) / 2}%`,
      maxHeight: "100%",
    };
  })();

  const {
    primaryTitle,
    description,
    link,
    image: desktopImageSrc,
    mobileImage: mobileImageSrc,
    altText,
    mobileAltText,
  } = tile.getData();

  const condensedMobileImageSrc = mobileImageSrc || desktopImageSrc;
  const condensedMobileImageAlt = mobileAltText || altText;

  if (!link) return null;

  const linkRef = GetGeneralLinkUrl(link, page);

  const handleTileClick = () => {
    sendDataToGTM({
      event: "tileClick",
      tileTitle: `${tile.primaryTitle ? tile.primaryTitle : tile.secondaryTitle}`,
      label: `${getPageTitle(page)}`,
    });
  };

  return (
    <LinkWrapper
      link={link}
      className={`${linkRef ? "tile--cta" : ""} tile tile--border-radius tile--${componentStyle} ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}
      style={tileStyleOverrides}
      onClick={() => handleTileClick()}
    >
      {isCondensed && !noImage && desktopImageSrc && (
        <CondensedDesktop>
          <div className={`tile__image-wrapper-head ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}>
            <Image
              src={desktopImageSrc}
              className="tile__image"
              alt={altText}
              height={basicHeight}
              width={basicWidth}
              overrideDimensions={null}
            />
          </div>
        </CondensedDesktop>
      )}
      {isCondensed && !noImage && condensedMobileImageSrc && (
        <CondensedMobile>
          <div className={`tile__image-wrapper-head ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}>
            <Image
              src={condensedMobileImageSrc}
              className="tile__image"
              alt={condensedMobileImageAlt}
              height={basicHeight}
              width={basicWidth}
              overrideDimensions={condensedImageOverride}
            />
          </div>
        </CondensedMobile>
      )}

      {primaryTitle && (
        <Title defaultLevel={HEADING_LEVELS.THREE} className={`tile__header tile--padding-side ${isCondensed ? "condensed" : ""}  ${resolvedCondensedStyle}`}>
          {primaryTitle}
        </Title>
      )}
      {description?.value && (
        <Description className={`tile__description tile--padding-side ${isCondensed ? "condensed" : ""}  ${resolvedCondensedStyle}`} value={description.value} />
      )}
      {!isCondensed && !noImage && desktopImageSrc && (
        <Desktop>
          <div className={`tile__image-wrapper ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}>
            <Image
              src={desktopImageSrc}
              className={`tile__image tile--border-radius-bottom ${isCondensed ? "condensed" : ""}  ${resolvedCondensedStyle}`}
              alt={altText}
              height={basicHeight}
              width={basicWidth}
              overrideDimensions={null}
            />
          </div>
        </Desktop>
      )}
      {!isCondensed && !noImage && mobileImageSrc && (
        <Mobile>
          <div className={`tile__image-wrapper ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}>
            <Image
              src={mobileImageSrc}
              className={`tile__image tile--border-radius-bottom ${isCondensed ? "condensed" : ""}  ${resolvedCondensedStyle}`}
              alt={!mobileAltText ? altText : mobileAltText}
              height={basicHeight}
              width={basicWidth}
              overrideDimensions={condensedImageOverride}
            />
          </div>
        </Mobile>
      )}
      {isCondensed && !noImage && desktopImageSrc && (
        <CondensedDesktop>
          <div className={`tile__image-wrapper ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}>
            <Image
              src={desktopImageSrc}
              className={`tile__image tile--border-radius-bottom ${isCondensed ? "condensed" : ""}  ${resolvedCondensedStyle}`}
              alt={altText}
              height={basicHeight}
              width={basicWidth}
              overrideDimensions={null}
            />
          </div>
        </CondensedDesktop>
      )}
      {isCondensed && !noImage && condensedMobileImageSrc && (
        <CondensedMobile>
          <div className={`tile__image-wrapper ${isCondensed ? "condensed" : ""} ${resolvedCondensedStyle}`}>
            <Image
              src={condensedMobileImageSrc}
              className={`tile__image tile--border-radius-bottom ${isCondensed ? "condensed" : ""}  ${resolvedCondensedStyle}`}
              alt={condensedMobileImageAlt}
              height={basicHeight}
              width={basicWidth}
              overrideDimensions={condensedImageOverride}
            />
          </div>
        </CondensedMobile>
      )}
      {isCondensed && (
        <CondensedDesktop>
          <div className="tile__condensed-action-arrow">&rsaquo;</div>
        </CondensedDesktop>
      )}
      {isCondensed && (
        <CondensedMobile>
          <button className="tile__condensed-mobile-button">{tileMobileButtonText || tileMobileButtonTextDefault || "Learn More"}</button>
        </CondensedMobile>
      )}
    </LinkWrapper>
  );
}

export default BasicContentTile;
